import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { yupResolver } from '@hookform/resolvers/yup'

import { Button } from '../../../../components/Button'
import { LineData } from '../../../../components/LineData'
import { type ExpertDomain } from '../../../../types/domain/expert-domain'
import {
  type InternationalPersonFormData,
  InternationalPersonFormPresentational,
} from '../../../../components/Forms/InternationalFormPresentational'
import { useExpertInternationalDataMutation } from '../../../../services/hooks/experts/mutations/useExpertInternationalDataMutation'
import { TOAST_SUCCESS_DEFAULT_OPTIONS } from '../../../../constants/toast'
import { internationalPersonSchema } from '../../../../validators/internationalPersonSchema'
import { useQueryClient } from 'react-query'
import countryAndNationalityList from '../../../../mocks/countryAndNationalityList'

interface InternationalPersonEditViewProps {
  expert?: ExpertDomain
}

export const InternationalPersonEditView = ({
  expert,
}: InternationalPersonEditViewProps) => {
  const [isFormVisible, setIsFormVisible] = useState(false)
  const queryClient = useQueryClient()

  const { formatMessage, locale } = useIntl()
  const { mutate, isLoading } = useExpertInternationalDataMutation()

  const getExpertCountryName = useMemo(() => {
    const country = countryAndNationalityList.find(
      (country) => country.alpha_3_code === expert?.naturalData?.country
    )
    if (!country) return ''

    return (
      country[`country_${locale as 'pt' | 'en' | 'es'}`] ?? country.country_en
    )
  }, [locale])

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(internationalPersonSchema),
    defaultValues: {
      name: expert?.name,
      email: expert?.email,
      phone: expert?.phone,
      linkedin: expert?.linkedin,
      city: expert?.naturalData?.city,
      state: expert?.naturalData?.state,
      country: expert?.naturalData?.country,
    },
  })

  const handleCancel = () => {
    methods.reset()
    setIsFormVisible(false)
  }

  const handleSubmit = (data: InternationalPersonFormData) => {
    mutate(
      { id: expert!.id, data },
      {
        onSuccess: () => {
          setIsFormVisible(false)
          queryClient.invalidateQueries('expert')
          toast.success(
            formatMessage({ id: 'DATA_SUCCESSFULLY_SAVED' }),
            TOAST_SUCCESS_DEFAULT_OPTIONS
          )
        },
      }
    )
  }

  if (isFormVisible) {
    return (
      <InternationalPersonFormPresentational
        {...methods}
        isSubmitting={isLoading}
        submitButtonText={formatMessage({ id: 'SAVE' })}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
      />
    )
  }

  return (
    <>
      <div>
        <LineData
          label={formatMessage({ id: 'FULL_NAME' })}
          text={expert?.name}
        />

        <div className="flex flex-col md:flex-row md:gap-14">
          <LineData label={'E-mail'} text={expert?.email} />
          <LineData
            label={formatMessage({ id: 'TELEPHONE' })}
            text={expert?.phone}
          />
        </div>
        <LineData
          label={formatMessage({ id: 'MEETING_LANGUAGES' })}
          text={
            expert?.meetingLanguages
              ?.map(
                (language) =>
                  language.charAt(0).toUpperCase() + language.slice(1)
              )
              .join(', ') || null
          }
        />

        <LineData label={'LinkedIn'} text={expert?.linkedin} />

        <LineData
          label={formatMessage({ id: 'CITY' })}
          text={expert?.naturalData?.city}
        />

        <LineData
          label={formatMessage({ id: 'STATE' })}
          text={expert?.naturalData?.state}
        />

        <LineData
          label={formatMessage({ id: 'COUNTRY' })}
          text={getExpertCountryName}
        />
      </div>

      <div className="w-full md:w-fit mt-14">
        <Button fullWidth onClick={() => setIsFormVisible(true)}>
          {formatMessage({ id: 'EDIT_DATA' })}
        </Button>
      </div>
    </>
  )
}
